import {
  ScoringConfig,
  RoundScore,
  SpeedBonusCalculation,
  DEFAULT_SCORING_CONFIG,
  SCORING_LIMITS
} from '@/types/ScoringTypes';
import { Question } from '@/types';

/**
 * Advanced scoring calculator with teacher-configurable parameters
 * Handles relative speed bonuses, point multipliers, and detailed score breakdowns
 */
export class ScoreCalculator {
  private config: ScoringConfig;

  constructor(config: ScoringConfig = DEFAULT_SCORING_CONFIG) {
    this.config = this.validateConfig(config);
  }

  /**
   * Calculate detailed score for a single player's round performance
   * Includes relative speed bonus based on how player performed vs others
   */
  calculateRoundScore(
    _playerId: string,
    question: Question,
    selectedAnswer: number,
    responseTime: number,
    allPlayerResponses: Map<string, { selectedAnswer: number; responseTime: number; isCorrect: boolean }>,
    roundNumber: number
  ): RoundScore {
    const isCorrect = selectedAnswer === question.correctAnswerIndex;
    const questionMultiplier = question.pointMultiplier ?? 1;

    // Base points calculation
    const basePoints = isCorrect ? this.config.basePoints : 0;

    // Speed bonus calculation (only for correct answers)
    let speedBonus = 0;
    let playerRank = 0;
    let totalCorrectAnswers = 0;

    if (isCorrect && this.config.enableSpeedBonus) {
      const speedData = this.calculateSpeedBonus(
        responseTime,
        allPlayerResponses
      );
      speedBonus = speedData.bonusAmount;
      playerRank = speedData.playersFasterCount + 1; // Rank starts at 1
      totalCorrectAnswers = speedData.totalCorrectPlayers;
    }

    // Apply question multiplier (0x, 1x, 2x, 3x points)
    const multipliedBasePoints = basePoints * questionMultiplier;
    const multipliedSpeedBonus = speedBonus * questionMultiplier;

    // Calculate multiplier bonus (extra points from 2x, 3x questions)
    const multiplierBonus = questionMultiplier > 1
      ? (questionMultiplier - 1) * (basePoints + speedBonus)
      : 0;

    // Penalties (future feature)
    let penalty = 0;
    if (!isCorrect && this.config.penalizeIncorrect) {
      penalty = this.config.incorrectPenalty;
    }

    // Final score calculation
    const finalScore = Math.max(0, multipliedBasePoints + multipliedSpeedBonus - penalty);

    return {
      roundNumber,
      questionId: question.id,
      basePoints: multipliedBasePoints,
      speedBonus: multipliedSpeedBonus,
      streakBonus: 0, // Future feature
      multiplierBonus,
      penalty,
      finalScore,
      responseTime,
      isCorrect,
      selectedAnswer,
      playerRank,
      totalCorrectAnswers
    };
  }

  /**
   * Calculate relative speed bonus based on player performance vs others
   * Only players faster than the median get bonus points (max 10% of base)
   */
  private calculateSpeedBonus(
    playerTime: number,
    allResponses: Map<string, { selectedAnswer: number; responseTime: number; isCorrect: boolean }>
  ): SpeedBonusCalculation {
    // Get all correct answer times for comparison
    const correctResponseTimes: number[] = [];

    for (const [, response] of allResponses) {
      if (response.isCorrect) {
        correctResponseTimes.push(response.responseTime);
      }
    }

    // If no one got it right or only one player (no competition), no speed bonus
    if (correctResponseTimes.length <= 1) {
      return {
        playerTime,
        fastestCorrectTime: playerTime,
        medianCorrectTime: playerTime,
        playersFasterCount: 0,
        totalCorrectPlayers: correctResponseTimes.length,
        bonusEligible: false,
        bonusAmount: 0
      };
    }

    // Calculate competitive metrics
    correctResponseTimes.sort((a, b) => a - b);
    const fastestTime = correctResponseTimes[0];
    const medianIndex = Math.floor(correctResponseTimes.length / 2);
    const medianTime = correctResponseTimes[medianIndex];

    // Count how many players were faster than current player
    const fasterPlayersCount = correctResponseTimes.filter(time => time < playerTime).length;

    // Player must be faster than median to get speed bonus
    const bonusEligible = playerTime < medianTime;

    let bonusAmount = 0;
    if (bonusEligible && fastestTime < playerTime) {
      // Speed bonus formula: relative improvement from median, capped at max percent
      const timeImprovement = Math.max(0, medianTime - playerTime);
      const maxPossibleImprovement = medianTime - fastestTime;

      if (maxPossibleImprovement > 0) {
        const improvementRatio = timeImprovement / maxPossibleImprovement;
        const maxBonus = (this.config.basePoints * this.config.speedBonusMaxPercent) / 100;
        bonusAmount = Math.floor(improvementRatio * maxBonus);
      }
    }

    return {
      playerTime,
      fastestCorrectTime: fastestTime,
      medianCorrectTime: medianTime,
      playersFasterCount: fasterPlayersCount,
      totalCorrectPlayers: correctResponseTimes.length,
      bonusEligible,
      bonusAmount
    };
  }

  /**
   * Calculate cumulative scores up to a specific round
   */
  calculateCumulativeScores(roundScores: RoundScore[]): number {
    return roundScores.reduce((total, round) => total + round.finalScore, 0);
  }

  /**
   * Get default base points for a question with multiplier applied
   */
  getQuestionMaxPoints(question: Question): number {
    const multiplier = question.pointMultiplier ?? 1;
    const baseWithBonus = this.config.basePoints * (1 + this.config.speedBonusMaxPercent / 100);
    return Math.floor(baseWithBonus * multiplier);
  }

  /**
   * Update scoring configuration (for teacher dashboard integration)
   */
  updateConfig(newConfig: Partial<ScoringConfig>): void {
    this.config = this.validateConfig({ ...this.config, ...newConfig });
  }

  /**
   * Get current configuration (for teacher dashboard display)
   */
  getConfig(): ScoringConfig {
    return { ...this.config };
  }

  /**
   * Validate scoring configuration to ensure safe values
   */
  private validateConfig(config: ScoringConfig): ScoringConfig {
    const validated = { ...config };

    // Validate base points
    validated.basePoints = Math.max(
      SCORING_LIMITS.MIN_BASE_POINTS,
      Math.min(SCORING_LIMITS.MAX_BASE_POINTS, validated.basePoints)
    );

    // Validate speed bonus percentage (hard limit: 10%)
    validated.speedBonusMaxPercent = Math.max(
      SCORING_LIMITS.MIN_SPEED_BONUS_PERCENT,
      Math.min(SCORING_LIMITS.MAX_SPEED_BONUS_PERCENT, validated.speedBonusMaxPercent)
    );

    // Validate streak bonus
    validated.streakBonusPoints = Math.max(
      0,
      Math.min(SCORING_LIMITS.MAX_STREAK_BONUS, validated.streakBonusPoints)
    );

    // Validate penalty
    validated.incorrectPenalty = Math.max(
      0,
      Math.min(SCORING_LIMITS.MAX_INCORRECT_PENALTY, validated.incorrectPenalty)
    );

    return validated;
  }

  /**
   * Create a scoring summary for teacher dashboard display
   */
  createScoringSummary(allRoundScores: RoundScore[]): {
    totalQuestions: number;
    correctAnswers: number;
    totalBasePoints: number;
    totalSpeedBonus: number;
    totalMultiplierBonus: number;
    finalScore: number;
    averageResponseTime: number;
  } {
    const totalQuestions = allRoundScores.length;
    const correctAnswers = allRoundScores.filter(r => r.isCorrect).length;
    const totalBasePoints = allRoundScores.reduce((sum, r) => sum + r.basePoints, 0);
    const totalSpeedBonus = allRoundScores.reduce((sum, r) => sum + r.speedBonus, 0);
    const totalMultiplierBonus = allRoundScores.reduce((sum, r) => sum + r.multiplierBonus, 0);
    const finalScore = allRoundScores.reduce((sum, r) => sum + r.finalScore, 0);
    const averageResponseTime = totalQuestions > 0
      ? allRoundScores.reduce((sum, r) => sum + r.responseTime, 0) / totalQuestions
      : 0;

    return {
      totalQuestions,
      correctAnswers,
      totalBasePoints,
      totalSpeedBonus,
      totalMultiplierBonus,
      finalScore,
      averageResponseTime
    };
  }
}
